import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { ChatEvent, ChatConnectionEvent, ChatMessage, Platform } from '@multimod/shared';

export const RING_SIZE = 1000;
/**
 * TTL (ms) para marcas optimistic de ban. Após esse tempo, o usuário sai do
 * `bannedUsers` automaticamente — reconcilia o estado com desban externo
 * (Twitch Dashboard / YouTube Studio).
 */
export const BAN_TTL_MS = 5 * 60_000;
/**
 * Janela (ms) pra dedup de WS contra optimistic broadcast pendente, usada APENAS
 * no caminho legado (quando o self externalId ainda não carregou). Após esse
 * tempo, o evento é tratado como mensagem nova (não-reconcile).
 *
 * O risco residual do #146 (viewer real mandando o MESMO texto DENTRO da janela)
 * foi resolvido (#149): quando `selfExternalIds[platform]` é conhecido, o match
 * por identidade do autor (`viewerWithKnownSelf` no `push`) impede que a msg de um
 * viewer case com o optimistic — só o eco do próprio mod reconcilia. A janela de 3s
 * sobra apenas como fallback pro intervalo curto até a sessão carregar o self id.
 */
export const RECONCILE_WINDOW_MS = 3_000;
/**
 * Janela (ms) pra reconciliar o eco quando a IDENTIDADE do autor prova que é a
 * própria mensagem do mod (`event.author.externalId === selfExternalIds[platform]`).
 * Bem mais generosa que `RECONCILE_WINDOW_MS` porque a identidade já remove a
 * ambiguidade — a janela aqui só evita casar com um optimistic antigo de mesma
 * frase. Cobre a latência real observada do EventSub + round-trip cross-platform
 * (visto ~12s em campo). Após isso, uma msg do mod no chat nativo volta a
 * renderizar normal mesmo com texto repetido.
 */
export const ECHO_RECONCILE_WINDOW_MS = 30_000;
/**
 * Cap do buffer de pause. Drop FIFO ao atingir. Limite mantém custo do push
 * pausado em O(CAP) por chamada e o tempo de replay em resume() limitado a um
 * único spread de até CAP itens. Em chat agressivo (~200 msg/min), 500 cobre
 * ~2.5 min de pause sem perder msg — suficiente pra ler msg longa.
 */
export const PENDING_BUFFER_CAP = 500;

/**
 * Fallback do displayName da mensagem otimista quando o nome real do mod ainda
 * não carregou da sessão (`selfDisplayNames` vazio). Em produção o PanelSession
 * injeta o nome real, então isso só aparece na janela curta antes do `me` chegar.
 * i18n: string em pt-BR — único ponto a tocar se a UI ganhar localização (#81).
 */
export const SELF_DISPLAY_NAME_FALLBACK = 'Você';

export type PendingAction = {
  kind: 'ban' | 'timeout' | 'delete';
  msgKey: string;
};

export type OptimisticBroadcastStatus = 'sending' | 'sent' | 'failed';

/**
 * Estado de conexão por plataforma, derivado dos eventos de connection do WS
 * (que carregam `platform`). Ver `docs/design/2026-05-30-status-conexao-por-plataforma.md`.
 *
 * `disconnected` NÃO é sempre falha: depende do `reason`. Terminação normal
 * (`live_ended`, `detached`) vira `ended` (terminal, não-destrutivo); erro real
 * (`auth`/`network`/`rate_limit`/`quota_exhausted`/`unknown`/ausente) vira
 * `failed`. Pintar `live_ended` de vermelho contradiz P13 (cor é semântica).
 */
export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'ended';
export type PlatformConnState = {
  state: ConnState;
  retryInMs?: number;
  // Enum fechado do schema — preserva exhaustiveness e habilita o Record de copy.
  reason?: ChatConnectionEvent['reason'];
};

/** reasons de `disconnected` que são terminação esperada, não erro. */
const NON_FAILURE_REASONS: ReadonlySet<NonNullable<ChatConnectionEvent['reason']>> = new Set([
  'live_ended',
  'detached',
]);

function connStateFromEvent(
  subtype: 'connecting' | 'connected' | 'reconnecting' | 'disconnected',
  reason: ChatConnectionEvent['reason'],
): ConnState {
  if (subtype !== 'disconnected') return subtype;
  return reason !== undefined && NON_FAILURE_REASONS.has(reason) ? 'ended' : 'failed';
}

export type PlatformFilter = 'all' | 'twitch' | 'youtube';
export interface ChatFeedFilter {
  platform: PlatformFilter;
  onlyMods: boolean;
}
const DEFAULT_FILTER: ChatFeedFilter = { platform: 'all', onlyMods: false };

export type OptimisticBroadcast = {
  status: OptimisticBroadcastStatus;
  createdAt: number;
  /**
   * Vira true quando o eco da plataforma já foi reconciliado contra este
   * broadcast. Impede que um SEGUNDO envio do mod com o mesmo texto (ex.: digitar
   * a mesma frase no chat nativo da Twitch logo depois) seja dropado por engano —
   * cada optimistic consome no máximo um eco.
   */
  echoConsumed?: boolean;
};

export type PushOptimisticBroadcastInput = {
  platform: Platform;
  liveChatId: string;
  externalId: string;
  text: string;
  now?: number;
};

export interface ChatFeedState {
  messages: ChatEvent[];
  status: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  lastReason: string | undefined;
  attempts: number | undefined;
  retryInMs: number | undefined;
  /**
   * Em modo attach (2 WS na mesma sala) o DO broadcastsa pra ambos os clients,
   * então cada mensagem chega duas vezes. Set acompanha mensagens já ingeridas
   * por (platform, externalId); evicção quando a mensagem sai do ring buffer.
   */
  _seen: Set<string>;
  /**
   * Chaves `${platform}:${externalId}` de mensagens marcadas como apagadas
   * (delete optimistic). Componente de mensagem renderiza placeholder.
   */
  deleted: Set<string>;
  /**
   * Mapa `${platform}:${externalUserId}` → expiresAt (timestamp ms) de usuários
   * marcados como banidos (ban optimistic).
   *
   * Bug C (2026-05-18): o estado era um `Set<string>` que nunca era limpo —
   * desban externo (Twitch Dashboard / YouTube Studio) deixava o painel com
   * msgs novas do usuário marcadas como BANNED indefinidamente. Agora cada
   * marca expira em `BAN_TTL_MS` (5 min) e pode ser limpa manualmente clicando
   * no badge `banned`. `purgeExpiredBans()` é chamado por tick na ChatFeed.
   */
  bannedUsers: Map<string, number>;
  /**
   * Mapa `${platform}:${externalUserId}` → expiresAt (timestamp ms) de usuários
   * marcados como em timeout (silenciados temporariamente).
   *
   * Bug D (2026-05-18): timeout era silencioso — mod aplicava timeout e nada
   * mudava no feed, sem confirmação visual. Agora cada timeout marca o autor
   * com TTL natural (`expiresAt = now + durationSeconds*1000`), `ChatMessageRow`
   * exibe badge `TIMED OUT` enquanto a marca está ativa, e `purgeExpiredTimeouts`
   * remove via tick global em `ChatFeed`.
   */
  timedOutUsers: Map<string, number>;
  optimisticBroadcasts: Map<string, OptimisticBroadcast>;
  /**
   * `externalId` do próprio mod por plataforma (vem de `me.connections[platform]`).
   * Usado pra reconciliar o eco da própria mensagem por identidade (não por id da
   * plataforma nem por janela de tempo). Vazio até a sessão carregar.
   */
  selfExternalIds: Partial<Record<Platform, string>>;
  /**
   * `displayName` real do próprio mod por plataforma (vem de `me.connections[platform]`).
   * Usado pela mensagem otimista do broadcast pra mostrar o nome real do mod em vez
   * do literal 'Você' (#81), preservando paridade visual com as msgs não-otimistas
   * dele que vêm do upstream com o nome real. Vazio até a sessão carregar.
   */
  selfDisplayNames: Partial<Record<Platform, string>>;
  /**
   * Estado de conexão por plataforma (idle/connecting/connected/reconnecting/
   * failed). No modo attach as 2 plataformas têm estados independentes; o
   * `status` único acima fica como compat. Alimentado pelos eventos de
   * connection em `push()`, indexado por `event.platform`.
   */
  connectionByPlatform: Partial<Record<Platform, PlatformConnState>>;
  /**
   * Quando `paused`, novos eventos de mensagem vão pra `pendingBuffer` em vez
   * de `messages` (UI fica estável pro mod ler msg longa). Eventos de connection
   * passam normais — status precisa refletir realidade. `resume()` libera o
   * buffer através do mesmo push() pra preservar dedup contra `messages`.
   */
  paused: boolean;
  pendingBuffer: ChatEvent[];
  /**
   * Contador de mensagens dropadas do pendingBuffer por atingir o cap durante
   * pause. UI usa pra avisar "buffer cheio" no botão de retomar. Zera em
   * resume(), clearLocal() e reset().
   */
  bufferDroppedCount: number;
  /**
   * Filtros aplicados pelo ChatFeed antes de passar pra virtualização.
   * - `platform: 'twitch'|'youtube'` esconde a outra; `all` mostra tudo.
   * - `onlyMods: true` mostra apenas msgs em que author é moderator ou broadcaster.
   * Filtros vivem no store pra persistir entre renders e ser introspeccionável
   * em testes e na popover de Filtros do ChannelSummary.
   */
  filter: ChatFeedFilter;
  /**
   * Índice da mensagem selecionada via navegação ↑↓ por teclado.
   * null = nenhuma mensagem selecionada.
   */
  selectedIndex: number | null;
  /**
   * Ação pendente despachada via hotkey (B/T/D).
   * Cada ChatMessageRow observa e abre o dialog/popover se o msgKey bater.
   */
  pendingAction: PendingAction | null;
  setSelfExternalIds: (ids: Partial<Record<Platform, string>>) => void;
  setSelfDisplayNames: (names: Partial<Record<Platform, string>>) => void;
  /**
   * Lookup O(1) de mensagem por `${platform}:${externalId}`. Evita o `messages.find`
   * O(n) por teclada de hotkey B/T/D (#50). Índice mantido em sincronia com
   * `messages` dentro do store (push/reconcile/eviction/clear).
   */
  getMessageByKey: (key: string) => ChatMessage | null;
  push: (event: ChatEvent) => void;
  pause: () => void;
  resume: () => void;
  clearLocal: () => void;
  setFilter: (patch: Partial<ChatFeedFilter>) => void;
  setLastReason: (reason: string) => void;
  markDeleted: (platform: 'twitch' | 'youtube', externalId: string) => void;
  unmarkDeleted: (platform: 'twitch' | 'youtube', externalId: string) => void;
  markBannedUser: (platform: 'twitch' | 'youtube', externalUserId: string) => void;
  unmarkBannedUser: (platform: 'twitch' | 'youtube', externalUserId: string) => void;
  purgeExpiredBans: (now?: number) => void;
  markTimedOutUser: (platform: 'twitch' | 'youtube', externalUserId: string, durationSeconds: number) => void;
  unmarkTimedOutUser: (platform: 'twitch' | 'youtube', externalUserId: string) => void;
  purgeExpiredTimeouts: (now?: number) => void;
  pushOptimisticBroadcast: (input: PushOptimisticBroadcastInput) => void;
  reconcileOptimisticBroadcast: (
    platform: Platform,
    optimisticExternalId: string,
    externalMessageId: string,
  ) => void;
  setOptimisticStatus: (
    platform: Platform,
    externalId: string,
    status: OptimisticBroadcastStatus,
  ) => void;
  purgeOptimisticBroadcasts: (now?: number, ttlMs?: number) => void;
  setSelectedIndex: (i: number | null) => void;
  setPendingAction: (a: PendingAction | null) => void;
  reset: () => void;
}

export type ChatFeedStore = UseBoundStore<StoreApi<ChatFeedState>>;

function messageKey(event: ChatEvent): string | null {
  if (event.type !== 'message') return null;
  return `${event.platform}:${event.externalId}`;
}

/**
 * Aplica o ring buffer ao array `messages` in-place: dropa o início até caber em
 * `RING_SIZE` e libera as chaves evictadas tanto do `seen` quanto do índice
 * `byKey`. Compartilhado por `push` e `pushOptimisticBroadcast` pra que ambos
 * respeitem o cap (#79 elimina a duplicação; #80 cobre o broadcast otimista).
 */
function applyRing(
  messages: ChatEvent[],
  seen: Set<string>,
  byKey: Map<string, ChatMessage>,
): void {
  if (messages.length <= RING_SIZE) return;
  const dropCount = messages.length - RING_SIZE;
  const dropped = messages.splice(0, dropCount);
  for (const ev of dropped) {
    const k = messageKey(ev);
    if (k !== null) {
      seen.delete(k);
      byKey.delete(k);
    }
  }
}

export function createChatFeedStore(): ChatFeedStore {
  // Índice mensagem-por-chave pra lookup O(1) das hotkeys (#50). Vive na closure
  // (não no state reativo) e é mutado in-place: nenhum componente o assina, então
  // não precisa de clone imutável a cada push — o que manteria o custo do hot path
  // em O(n) por mensagem só pra acelerar uma tecla rara. Sincronizado nos pontos
  // que mexem em `messages`: push, reconcile, eviction (applyRing) e clear/reset.
  const messagesByKey = new Map<string, ChatMessage>();
  return create<ChatFeedState>((set, get) => ({
    messages: [],
    status: 'idle',
    lastReason: undefined,
    attempts: undefined,
    retryInMs: undefined,
    _seen: new Set<string>(),
    deleted: new Set<string>(),
    bannedUsers: new Map<string, number>(),
    timedOutUsers: new Map<string, number>(),
    optimisticBroadcasts: new Map<string, OptimisticBroadcast>(),
    selfExternalIds: {},
    selfDisplayNames: {},
    connectionByPlatform: {},
    paused: false,
    pendingBuffer: [],
    bufferDroppedCount: 0,
    filter: DEFAULT_FILTER,
    selectedIndex: null,
    pendingAction: null,
    setSelfExternalIds: (ids) => set({ selfExternalIds: ids }),
    setSelfDisplayNames: (names) => set({ selfDisplayNames: names }),
    getMessageByKey: (key) => messagesByKey.get(key) ?? null,
    push: (event) => set((s) => {
      // Quando pausado, mensagens não entram em `messages` — vão pro
      // pendingBuffer pra liberar no resume(). Eventos de connection
      // passam direto pra manter status real visível.
      if (s.paused && event.type === 'message') {
        if (s.pendingBuffer.length >= PENDING_BUFFER_CAP) {
          // Drop FIFO quando cheio. Mantém custo do push pausado constante e
          // limita o tamanho do replay em resume().
          const nextBuffer = s.pendingBuffer.slice(1);
          nextBuffer.push(event);
          return {
            pendingBuffer: nextBuffer,
            bufferDroppedCount: s.bufferDroppedCount + 1,
          };
        }
        return { pendingBuffer: [...s.pendingBuffer, event] };
      }
      const key = messageKey(event);
      if (key !== null && s._seen.has(key)) return {};

      // Reconciliação do eco da PRÓPRIA mensagem do mod contra o optimistic
      // pendente — senão duplica no feed (uma "Você" + uma com o nome real).
      //
      // Duas faces da corrida optimistic↔eco:
      //  (A) HTTP responde antes do eco → `reconcileOptimisticBroadcast` já trocou
      //      `optimistic:` pelo message_id do Helix. O eco chega depois com o
      //      message_id do EventSub, que a Twitch NÃO garante ser igual ao do
      //      Helix Send → `_seen` não pega. Aqui a identidade do autor resolve.
      //  (B) Eco chega antes do HTTP → reconcilia trocando o id pelo real do eco.
      //
      // Sinal forte = identidade: `event.author.externalId === selfExternalIds`.
      // Com ela, dispensamos a janela de 3s (frágil a clock skew: timestamp do
      // evento é hora do servidor, createdAt é do cliente) e o match por id
      // (não-documentado). Sem o self id (sessão não carregou), cai no caminho
      // legado (prefixo `optimistic:` + janela de 3s).
      //
      // Guarda `size > 0` evita O(n) à toa quando não há broadcast pendente.
      if (
        event.type === 'message' &&
        key !== null &&
        s.optimisticBroadcasts.size > 0
      ) {
        const selfId = s.selfExternalIds[event.platform];
        const isSelfEcho = selfId !== undefined && event.author.externalId === selfId;
        // Quando sabemos o id do mod e o autor NÃO é ele, é viewer real:
        // nunca reconcilia (corrige o risco residual do TODO #146 de comer a
        // mensagem de um viewer que repetiu o mesmo texto dentro da janela).
        const viewerWithKnownSelf = selfId !== undefined && !isSelfEcho;
        // Relógio do CLIENTE pra medir a janela do self-echo. `event.timestamp` é
        // hora do servidor (Twitch/YouTube) e pode estar adiantado em dezenas de
        // segundos vs o cliente (clock skew observado: ~60s no WSL) — usá-lo aqui
        // estoura a janela e a própria msg do mod duplica. `createdAt` também é do
        // cliente, então `nowMs - createdAt` mede o tempo real decorrido, imune a skew.
        const nowMs = Date.now();
        if (!viewerWithKnownSelf) {
          const optimistic = s.messages.find((m) => {
            if (
              m.type !== 'message' ||
              m.platform !== event.platform ||
              m.text !== event.text ||
              m.author.externalId !== 'self'
            ) {
              return false;
            }
            const ob = s.optimisticBroadcasts.get(`${event.platform}:${m.externalId}`);
            if (!ob || ob.echoConsumed) return false;
            if (isSelfEcho) {
              return nowMs - ob.createdAt <= ECHO_RECONCILE_WINDOW_MS;
            }
            // Legado (self id desconhecido): exige o prefixo `optimistic:` (ainda
            // não reconciliado pelo HTTP) e a janela estreita de 3s.
            return (
              m.externalId.startsWith('optimistic:') &&
              event.timestamp - ob.createdAt <= RECONCILE_WINDOW_MS
            );
          });
          if (optimistic && optimistic.type === 'message') {
            const oldKey = `${event.platform}:${optimistic.externalId}`;
            const seen = new Set(s._seen);
            const optimisticBroadcasts = new Map(s.optimisticBroadcasts);
            const prev = optimisticBroadcasts.get(oldKey);
            if (optimistic.externalId.startsWith('optimistic:')) {
              // Face (B): ainda não reconciliado pelo HTTP — troca o id pelo real
              // do eco e marca como enviado/consumido.
              const newKey = key;
              seen.delete(oldKey);
              seen.add(newKey);
              if (prev) {
                optimisticBroadcasts.delete(oldKey);
                optimisticBroadcasts.set(newKey, { ...prev, status: 'sent', echoConsumed: true });
              }
              const reconciled = { ...optimistic, externalId: event.externalId };
              const messages = s.messages.map((m) => (m === optimistic ? reconciled : m));
              // #50: índice acompanha a troca de id (a chave muda de optimistic:→real).
              messagesByKey.delete(oldKey);
              messagesByKey.set(newKey, reconciled);
              return { messages, _seen: seen, optimisticBroadcasts };
            }
            // Face (A): o HTTP já reconciliou (msg tem o id do Helix). Mantém a
            // mensagem como está e apenas consome/dropa o eco — registra o id do
            // eco no `_seen` pra também dropar a 2ª cópia do attach (2º WS).
            seen.add(key);
            if (prev) optimisticBroadcasts.set(oldKey, { ...prev, echoConsumed: true });
            return { _seen: seen, optimisticBroadcasts };
          }
        }
      }

      const seen = new Set(s._seen);
      if (key !== null) seen.add(key);
      const messages = [...s.messages, event];
      if (key !== null && event.type === 'message') messagesByKey.set(key, event);
      // Ring buffer: dropa o início e libera chaves evictadas de seen + índice.
      applyRing(messages, seen, messagesByKey);

      if (event.type === 'connection') {
        const platformState: PlatformConnState = {
          state: connStateFromEvent(event.subtype, event.reason),
        };
        if (event.retryInMs !== undefined) platformState.retryInMs = event.retryInMs;
        if (event.reason !== undefined) platformState.reason = event.reason;
        return {
          messages,
          _seen: seen,
          status: event.subtype,
          lastReason: event.reason ?? s.lastReason,
          attempts: event.attempt,
          retryInMs: event.retryInMs,
          connectionByPlatform: {
            ...s.connectionByPlatform,
            [event.platform]: platformState,
          },
        };
      }
      return { messages, _seen: seen };
    }),
    setLastReason: (reason) => set({ lastReason: reason }),
    markDeleted: (platform, externalId) => set((s) => {
      const next = new Set(s.deleted);
      next.add(`${platform}:${externalId}`);
      return { deleted: next };
    }),
    unmarkDeleted: (platform, externalId) => set((s) => {
      const next = new Set(s.deleted);
      next.delete(`${platform}:${externalId}`);
      return { deleted: next };
    }),
    markBannedUser: (platform, externalUserId) => set((s) => {
      const next = new Map(s.bannedUsers);
      next.set(`${platform}:${externalUserId}`, Date.now() + BAN_TTL_MS);
      return { bannedUsers: next };
    }),
    unmarkBannedUser: (platform, externalUserId) => set((s) => {
      const next = new Map(s.bannedUsers);
      next.delete(`${platform}:${externalUserId}`);
      return { bannedUsers: next };
    }),
    purgeExpiredBans: (now = Date.now()) => set((s) => {
      let changed = false;
      const next = new Map(s.bannedUsers);
      for (const [key, expiresAt] of next) {
        if (expiresAt <= now) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? { bannedUsers: next } : {};
    }),
    markTimedOutUser: (platform, externalUserId, durationSeconds) => set((s) => {
      const next = new Map(s.timedOutUsers);
      next.set(`${platform}:${externalUserId}`, Date.now() + durationSeconds * 1000);
      return { timedOutUsers: next };
    }),
    unmarkTimedOutUser: (platform, externalUserId) => set((s) => {
      const next = new Map(s.timedOutUsers);
      next.delete(`${platform}:${externalUserId}`);
      return { timedOutUsers: next };
    }),
    purgeExpiredTimeouts: (now = Date.now()) => set((s) => {
      let changed = false;
      const next = new Map(s.timedOutUsers);
      for (const [key, expiresAt] of next) {
        if (expiresAt <= now) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? { timedOutUsers: next } : {};
    }),
    pushOptimisticBroadcast: (input) => set((s) => {
      const now = input.now ?? Date.now();
      const event: ChatMessage = {
        type: 'message',
        platform: input.platform,
        liveChatId: input.liveChatId,
        externalId: input.externalId,
        text: input.text,
        timestamp: now,
        author: {
          platform: input.platform,
          externalId: 'self',
          // #81: nome real do mod quando disponível; 'Você' só até o `me` carregar.
          displayName: s.selfDisplayNames[input.platform] ?? SELF_DISPLAY_NAME_FALLBACK,
        },
        isModerator: true,
        isBroadcaster: false,
      };
      const key = messageKey(event);
      const messages = [...s.messages, event];
      const seen = new Set(s._seen);
      if (key !== null) {
        seen.add(key);
        messagesByKey.set(key, event);
      }
      // #80: optimistic também respeita o cap do ring (evita crescimento ilimitado
      // do array em spam de broadcast — o purge só limpa o map, não as msgs).
      applyRing(messages, seen, messagesByKey);
      const optimisticBroadcasts = new Map(s.optimisticBroadcasts);
      optimisticBroadcasts.set(`${input.platform}:${input.externalId}`, {
        status: 'sending',
        createdAt: now,
      });
      return { messages, _seen: seen, optimisticBroadcasts };
    }),
    reconcileOptimisticBroadcast: (platform, optimisticExternalId, externalMessageId) => set((s) => {
      const optimisticKey = `${platform}:${optimisticExternalId}`;
      const realKey = `${platform}:${externalMessageId}`;
      const current = s.optimisticBroadcasts.get(optimisticKey);
      const optimisticBroadcasts = new Map(s.optimisticBroadcasts);
      optimisticBroadcasts.delete(optimisticKey);
      optimisticBroadcasts.set(realKey, {
        status: 'sent',
        createdAt: current?.createdAt ?? Date.now(),
      });
      const seen = new Set(s._seen);
      seen.delete(optimisticKey);
      seen.add(realKey);
      const messages = s.messages.map((event) => {
        if (
          event.type === 'message' &&
          event.platform === platform &&
          event.externalId === optimisticExternalId
        ) {
          const reconciled = { ...event, externalId: externalMessageId };
          // #50: índice acompanha a troca optimistic:→real.
          messagesByKey.delete(optimisticKey);
          messagesByKey.set(realKey, reconciled);
          return reconciled;
        }
        return event;
      });
      return { messages, _seen: seen, optimisticBroadcasts };
    }),
    setOptimisticStatus: (platform, externalId, status) => set((s) => {
      const key = `${platform}:${externalId}`;
      const current = s.optimisticBroadcasts.get(key);
      if (!current) return {};
      const optimisticBroadcasts = new Map(s.optimisticBroadcasts);
      optimisticBroadcasts.set(key, { ...current, status });
      return { optimisticBroadcasts };
    }),
    purgeOptimisticBroadcasts: (now = Date.now(), ttlMs = 10 * 60_000) => set((s) => {
      let changed = false;
      const optimisticBroadcasts = new Map(s.optimisticBroadcasts);
      for (const [key, item] of optimisticBroadcasts) {
        if (item.status !== 'sending' && item.createdAt + ttlMs <= now) {
          optimisticBroadcasts.delete(key);
          changed = true;
        }
      }
      return changed ? { optimisticBroadcasts } : {};
    }),
    setSelectedIndex: (i) => set({ selectedIndex: i }),
    setPendingAction: (a) => set({ pendingAction: a }),
    pause: () => set({ paused: true }),
    setFilter: (patch) => set((s) => ({ filter: { ...s.filter, ...patch } })),
    resume: () => {
      const { pendingBuffer } = get();
      // Transição atômica em 1 set: paused:false + zerar buffer + zerar drop
      // count. Depois replayamos via push(), que agora vê paused=false e
      // empurra pra messages com dedup/reconcile normais.
      //
      // Race teórico (apontado no review de PR #152): entre o set e o for um
      // push novo poderia entrar em `messages` antes dos buffered. Em prática,
      // JS é single-thread e eventos WS são async — não existe janela síncrona
      // entre `set()` e o `for` em que outro caller possa rodar.
      set({ paused: false, pendingBuffer: [], bufferDroppedCount: 0 });
      for (const event of pendingBuffer) {
        get().push(event);
      }
    },
    clearLocal: () => {
      messagesByKey.clear();
      set({
        messages: [],
        pendingBuffer: [],
        bufferDroppedCount: 0,
        _seen: new Set<string>(),
        deleted: new Set<string>(),
        bannedUsers: new Map<string, number>(),
        timedOutUsers: new Map<string, number>(),
        optimisticBroadcasts: new Map<string, OptimisticBroadcast>(),
        selectedIndex: null,
        pendingAction: null,
      });
    },
    reset: () => {
      messagesByKey.clear();
      set({
        messages: [],
        status: 'idle',
        lastReason: undefined,
        attempts: undefined,
        retryInMs: undefined,
        _seen: new Set<string>(),
        deleted: new Set<string>(),
        bannedUsers: new Map<string, number>(),
        timedOutUsers: new Map<string, number>(),
        optimisticBroadcasts: new Map<string, OptimisticBroadcast>(),
        selfExternalIds: {},
        selfDisplayNames: {},
        connectionByPlatform: {},
        paused: false,
        pendingBuffer: [],
        bufferDroppedCount: 0,
        filter: DEFAULT_FILTER,
        selectedIndex: null,
        pendingAction: null,
      });
    },
  }));
}
